<script setup>

import Avatar from "primevue/avatar";
import Password from "primevue/password";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import Button from "primevue/button";
import {usePage} from "@inertiajs/vue3";
import {useForm} from "laravel-precognition-vue-inertia";

const LOCALES = [
    { code: 'cs', label: 'Čeština', flag: '🇨🇿' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'ko', label: '한국어',   flag: '🇰🇷' },
]

const props = usePage().props;
const form = useForm('post', '/app/settings', {
    smtp_server: props.settings?.smtp_server,
    smtp_port: props.settings?.smtp_port,
    smtp_username: props.settings?.smtp_username,
    smtp_password: props.settings?.smtp_password,
    smtp_encryption: props.settings?.smtp_encryption,
    smtp_address: props.settings?.smtp_address,
    smtp_name: props.settings?.smtp_name,
    // User account
    user_name: props.settings?.user_name,
    user_password: '',
    user_locale: usePage().props.locale,
});
</script>

<template>
    <div class="w-full flex flex-col gap-8 h-full">
        <div class="flex items-center gap-3">
            <Avatar :label="$page.props.user_initials" size="xlarge" shape="circle"></Avatar>
            <div>
                <p class="text-xl font-bold">{{ $page.props.user.name }}</p>
                <a :href="`${$page.props.olympus.account_url}/user/profile`" target="_blank"
                   class="text-sm text-gray-600 dark:text-gray-200 cursor-pointer">
                    {{ $t('settings.maeum_account') }} <span class="pi pi-arrow-up-right !text-xs"></span>
                </a>
            </div>
        </div>

        <div class="flex flex-col items-start gap-1 w-1/4">
            <label for="smtp_address" class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ $t('settings.field_name') }}</label>
            <InputText id="smtp_address"
                       v-model="form.user_name"
                       fluid
                       @change="form.validate('user_name')"
                       :class="{'p-invalid': form.invalid('user_name')}">
            </InputText>
            <small v-if="form.invalid('user_name')" class="p-error">
                {{ form.errors['user_name'] }}
            </small>
        </div>

        <div class="flex flex-col items-start gap-1 w-1/4">
            <label for="smtp_address" class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ $t('settings.field_password') }}</label>
            <Password id="smtp_address"
                      v-model="form.user_password"
                      fluid
                      class="w-full"
                      @change="form.validate('user_password')"
                      :class="{'p-invalid': form.invalid('user_password')}">
            </Password>
            <small :class="form.invalid('user_password') ? `p-error` : ''">
                {{ form.errors['user_password'] ?? $t('settings.field_password_hint') }}
            </small>
        </div>

        <div class="flex flex-col items-start gap-1 w-1/4">
            <label for="user_locale" class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ $t('settings.field_language') }}</label>
            <Select id="user_locale"
                    v-model="form.user_locale"
                    :options="LOCALES"
                    option-label="label"
                    option-value="code"
                    fluid
                    @change="form.validate('user_locale')"
                    :class="{'p-invalid': form.invalid('user_locale')}">
                <template #value="{ value }">
                    <span v-if="value">{{ LOCALES.find(l => l.code === value)?.flag }} {{ LOCALES.find(l => l.code === value)?.label }}</span>
                </template>
                <template #option="{ option }">
                    <span>{{ option.flag }} {{ option.label }}</span>
                </template>
            </Select>
            <small v-if="form.invalid('user_locale')" class="p-error">
                {{ form.errors['user_locale'] }}
            </small>
        </div>

        <div class="text-center w-full relative">
            <div class=" absolute top-0 w-full z-10">
                <p class="mb-2">{{ $t('settings.additional_settings') }}</p>
                <a href="/profile" target="_blank">
                    <Button severity="contrast" :label="$t('settings.maeum_account')" size="small">
                        <template #icon>
                            <div class="flex flex-col items-center">
                                <div class="maeum-short"></div>
                                <div class="maeum-long"></div>
                                <div class="maeum-short"></div>
                            </div>
                        </template>
                    </Button>
                </a>
            </div>
            <div class="flex items-center blur-xl opacity-70 w-full justify-center scale-125">
                <div class="w-16 h-16 rounded-full bg-blue-500/60"></div>
                <div class="w-14 h-14 rounded-full bg-purple-500/80 -mt-3 -ml-5"></div>
                <div class="w-20 h-20 rounded-full bg-black/30 mt-3 -ml-5"></div>
            </div>
        </div>
        <div class="p-3 flex justify-end">
            <Button :label="$t('common.save')"
                    :loading="form.processing"
                    @click="() => {
                        form.touch(['user_name', 'user_password', 'user_locale']).submit()
                    }">
            </Button>
        </div>
    </div>
</template>

<style scoped lang="scss">

</style>
